import { ReactNode, useEffect } from "react";
import Header from "@/components/layout/homepage/Header";
import Sidebar from "@/components/layout/homepage/Sidebar";
import { useSidebar } from "@/contexts/SidebarContext";
import { useResponsive } from "@/hooks/useMobile";
import { cn } from "@/components/utils/utils";
import { useAuth } from "@/hooks/useAuth";
import MobileSidebarModal from "../homepage/MobileSidebarModal";

interface PrivateLayoutProps {
  children: ReactNode;
  className?: string;
  /**
   * Classi applicate al container interno che avvolge i children. Opt-in: se
   * omessa resta il default content-driven con margini verticali. Le pagine che
   * devono occupare tutta l'altezza disponibile (es. Library con tabella a
   * scroll interno) passano `h-full flex flex-col min-h-0` senza margini `my-*`.
   */
  contentClassName?: string;
  pageTitle?: ReactNode;
  pageDescription?: string;
  showBackButton?: boolean;
  headerAction?: ReactNode;
  showCtaButton?: boolean;
}

const PrivateLayout = ({ children, className = "", contentClassName, pageTitle, pageDescription, showBackButton = true, headerAction, showCtaButton = false }: PrivateLayoutProps) => {
  const { isSidebarCollapsed, isMobileSidebarOpen, setMobileSidebarOpen, toggleSidebar } = useSidebar();
  const { isMobile } = useResponsive();
  const { userRole, logoUrl: agencyLogoUrl, logoSmallUrl: agencyLogoSmallUrl, logoLoading } = useAuth();

  const handleMobileSidebarClose = () => {
    setMobileSidebarOpen(false);
  };

  // Chiudi il mobile sidebar quando si passa da mobile a desktop
  useEffect(() => {
    if (!isMobile && isMobileSidebarOpen) {
      setMobileSidebarOpen(false);
    }
  }, [isMobile, isMobileSidebarOpen, setMobileSidebarOpen]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header Fixed */}
      <Header 
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        showBackButton={showBackButton}
        headerAction={headerAction}
        showCtaButton={showCtaButton}
      />

      {/* Mobile Sidebar Modal */}
      <MobileSidebarModal 
        isOpen={isMobileSidebarOpen}
        onClose={handleMobileSidebarClose}
        userRole={userRole}
        agencyLogoUrl={agencyLogoUrl}
        agencyLogoSmallUrl={agencyLogoSmallUrl}
        logoLoading={logoLoading}
      />
      
      <div className="flex h-screen">
        {/* Desktop Sidebar */}
        <div className={cn(
          "hidden md:block h-full transition-all duration-300 ease-in-out",
          isSidebarCollapsed ? "w-[70px]" : "w-[222px]"
        )}>
          <Sidebar 
            isCollapsed={isSidebarCollapsed} 
            onToggle={toggleSidebar}
            userRole={userRole}
            agencyLogoUrl={agencyLogoUrl}
            agencyLogoSmallUrl={agencyLogoSmallUrl}
            logoLoading={logoLoading}
          />
        </div>
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Main Content */}
          <main className={cn(
            "flex-1 transition-all duration-300 ease-in-out overflow-auto mt-20 md:mt-[80px]",
            className
          )}>
            <div className={cn(
              "container mx-auto px-4 md:px-6",
              contentClassName ?? "my-2 md:my-6",
              contentClassName
            )}>
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PrivateLayout; 